export default class RangeGraphInput {
    constructor(container, updateCallback, currency) {
        this.startInputModified = false;
        this.endInputInputModified = false;
        this.rangeRound = 5;
        this.inputTimeout = null;
        this.currency = currency;
        this.container = container;
        this.updateCallback = updateCallback;
        this.sliderStart = this.container.querySelector('[data-slider="start"]');
        this.sliderEnd = this.container.querySelector('[data-slider="end"]');
        this.inputStart = this.container.querySelector('[data-slider-input="start"]');
        this.inputEnd = this.container.querySelector('[data-slider-input="end"]');
        this.graphContainer = this.container.querySelector('[data-container="graph"]');
        this.rangeGraphContainer = this.container.querySelector('[data-container="range-graph"]')
        this.foregroundColor = 'var(--secondary-light)';
        this.activeColor = 'var(--primary)';
        this.eventDelegation();
    }

    eventDelegation() {
        this.rangeGraphContainer.addEventListener('click', this._onGraphClick.bind(this));

        this.sliderStart.addEventListener('input', (e) => {
            clearTimeout(this.inputTimeout);
            this.startInputModified = true;
            let maxValue = parseInt(this.sliderEnd.value) - parseInt(this.step * 2);
            maxValue = Math.ceil(maxValue / this.rangeRound) * this.rangeRound;
            let startValue = Math.floor(parseInt(this.sliderStart.value / this.rangeRound) * this.rangeRound);
            this.sliderStart.value = (startValue >= maxValue) ? maxValue : startValue;
            this._controlFromSlider(this.sliderStart, this.sliderEnd, this.inputStart);
            this.inputTimeout = setTimeout(() => {
                this.updateCallback();
            }, 100);
        });
        this.sliderEnd.addEventListener('input', (e) => {
            clearTimeout(this.inputTimeout);
            this.endInputInputModified = true;
            let minValue = parseInt(this.sliderStart.value) + parseInt(this.step * 2);
            minValue = Math.floor(minValue / this.rangeRound) * this.rangeRound;
            let endValue = Math.ceil(this.sliderEnd.value / this.rangeRound) * this.rangeRound;
            this.sliderEnd.value = (endValue <= minValue) ? minValue : endValue;
            this._controlToSlider(this.sliderStart, this.sliderEnd, this.inputEnd);
            this.inputTimeout = setTimeout(() => {
                this.updateCallback();
            }, 100);
        });
    }

    generateRangeGraph(values, numBars) {
        values.sort((a, b) => a - b);
        let parameters = this._generateRangeParameters(values, numBars);
        let max = parameters.max;
        let min = parameters.min;
        let stepSize = parameters.stepSize;

        let bars = this._calculateBars(values, stepSize, numBars, min, max);
        this.sliderStart.max = max;
        this.sliderEnd.max = max;
        this.sliderStart.min = min;
        this.sliderEnd.min = min;
        this.step = stepSize;
        this._checkCurrentValues(min, max);
        this._generateGraphBars(bars, stepSize, numBars);
        this._fillSlider(this.sliderEnd);
    }

    _generateRangeParameters(values, numBars) {
        let max = this._calculateIdealMax(values);
        let min = values.length > 0 ? Math.min(...values) : 0;
        let currentMin = parseInt(this.sliderStart.value);
        let currentMax = parseInt(this.sliderEnd.value);
        if (this.startInputModified && min > currentMin) {
            min = currentMin;
        }
        if (this.endInputInputModified && max < currentMax) {
            max = currentMax;
        }
        if ((max - min) < ((numBars / 2) * this.rangeRound)) {
            min = min - (numBars * this.rangeRound);
            min = min < 0 ? 0 : min;
            max = max + (numBars * this.rangeRound);
        }

        min = Math.floor(min / this.rangeRound) * this.rangeRound;
        max = Math.ceil(max / this.rangeRound) * this.rangeRound;

        let stepSize = (max - min) / numBars;
        return {min, max, stepSize};
    }

    _checkCurrentValues(min, max) {
        if (!this.startInputModified && !this.endInputInputModified) {
            this.sliderStart.value = min;
            this._formatInput(this.inputStart, min);
            this.sliderEnd.value = max;
            this._formatInput(this.inputEnd, max);
        }
    }

    _calculateIdealMax(values) {
        if (values.length < 2) return Math.max(...values);
        let average = values.reduce((a, b) => a + b) / values.length;

        let maxValue = values.find((value) => value >= (average * 2) && values.indexOf(value) > (values.length / 2));
        return maxValue ?? Math.max(...values);
    }

    _onGraphClick(event) {
        if (event.target instanceof HTMLInputElement) {
            return;
        }
        const rect = this.rangeGraphContainer.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const totalWidth = rect.width;
        const clickPercent = (clickX / totalWidth) * 100;

        const min = parseInt(this.sliderStart.min);
        const max = parseInt(this.sliderEnd.max);
        const newValue = Math.round(min + ((max - min) * (clickPercent / 100)));

        const middleValue = (parseInt(this.sliderStart.value) + parseInt(this.sliderEnd.value)) / 2;
        if (newValue < middleValue) {
            this.sliderStart.value = newValue;
            this._controlFromSlider(this.sliderStart, this.sliderEnd, this.inputStart);
        } else {
            this.sliderEnd.value = newValue;
            this._controlToSlider(this.sliderStart, this.sliderEnd, this.inputEnd);
        }
        this.updateCallback();
    }

    _calculateBars(values, stepSize, numBars, min, max) {
        let value = min;
        let bars = [];
        let maxCount = 0;

        for (let i = 0; i < numBars; i++) {
            let minTmp = value;
            let maxTmp = (i == (numBars - 1)) ? max : (value + stepSize);
            let count = values.filter(value => value >= minTmp && (value < maxTmp || (i == (numBars - 1) && value >= maxTmp))).length;

            bars.push({
                data: {
                    min: minTmp,
                    max: maxTmp,
                },
                count: count
            });
            if (count > maxCount) {
                maxCount = count;
            }
            value += stepSize;
        }
        bars.forEach(bar => {
            bar['heightPercent'] = ((bar.count / maxCount) * 100);
        });

        return bars;
    }

    _formatInput(input, value) {
        input.innerHTML = this.currency + '' + value + '' + ((parseInt(value) >= parseInt(this.sliderEnd.max)) ? ' +' : '');
    }

    _barPaintCallback(bar) {
        let min = parseInt(bar.dataset.min);
        let max = parseInt(bar.dataset.max);
        let middleValue = (min + max) / 2;
        let startValue = parseInt(this.sliderStart.value);
        let endValue = parseInt(this.sliderEnd.value);

        if (startValue <= min && endValue >= max ||
            startValue >= min && startValue <= middleValue ||
            endValue <= max && endValue >= middleValue) {
            bar.style.backgroundColor = this.activeColor;
        } else {
            bar.style.backgroundColor = this.foregroundColor;
        }
    }

    _controlFromSlider(sliderStart, sliderEnd, inputStart) {
        const [from, to] = this._getParsed(sliderStart, sliderEnd);
        sliderStart.value = from > to ? to : from;
        this._formatInput(inputStart, sliderStart.value);
        this._fillSlider(sliderEnd);
    }

    _controlToSlider(sliderStart, sliderEnd, inputEnd) {
        const [from, to] = this._getParsed(sliderStart, sliderEnd);
        sliderEnd.value = from <= to ? to : from;
        this._formatInput(inputEnd, sliderEnd.value);
        this._fillSlider(sliderEnd);
        this._setToggleAccessible(sliderEnd);
    }

    _getParsed(currentFrom, currentTo) {
        const from = parseInt(currentFrom.dataset.value || currentFrom.value, 10);
        const to = parseInt(currentTo.dataset.value || currentTo.value, 10);
        return [from, to];
    }

    _fillSlider(controlSlider) {
        const graphBars = this.graphContainer.children;
        const startSliderPercent = this._getSliderPercent(this.sliderStart);
        const endSliderPercent = this._getSliderPercent(this.sliderEnd);

        Object.values(graphBars).forEach((bar, index) => {
            this._barPaintCallback(bar);
        });
        controlSlider.style.background = `
            linear-gradient(
                to right,
                ${this.foregroundColor} 0%,
                ${this.foregroundColor} ${startSliderPercent}%,
                ${this.activeColor} ${startSliderPercent}%,
                ${this.activeColor} ${endSliderPercent}%,
                ${this.foregroundColor} ${endSliderPercent}%,
                ${this.foregroundColor} 100%
            )
        `;
    }

    _getSliderPercent(slider) {
        const distanceFromMin = (parseInt(slider.value) - parseInt(slider.min));
        const sliderRange = (parseInt(slider.max) - parseInt(slider.min));
        return 100 * (distanceFromMin / sliderRange);
    }

    _setToggleAccessible(currentTarget) {
        if (Number(currentTarget.value) <= 0) {
            this.sliderEnd.style.zIndex = 2;
        } else {
            this.sliderEnd.style.zIndex = 0;
        }
    }

    _generateGraphBars(bars) {
        this._resetGraphBars();
        bars.forEach(bar => {
            this._appendGraphBar(bar);
        });
    }

    _resetGraphBars() {
        this.graphContainer.innerHTML = '';
    }

    _appendGraphBar(bar) {
        const graphBar = document.createElement('div');
        graphBar.classList.add('width-100', 'bg-primary', 'border-xs', 'border-light');
        graphBar.style.height = `${bar.heightPercent}%`;
        Object.entries(bar.data).forEach(([key, value]) => {
            graphBar.dataset[key] = value;
        });
        this.graphContainer.appendChild(graphBar);
    }
}
